/**
 * copilot-credits — the PURE rules behind BB Copilot's credit + split tools
 * (addContributor, setSplit). No Firebase, no I/O: everything here is unit-
 * tested (copilot-credits.test.ts) and shared by the plan preview and the
 * apply step, so what the plan card promises is exactly what Apply does.
 *
 *   • Contributors merge by name (case/accents/spacing-insensitive) or by
 *     linked account — a second credit for the same person unions roles and
 *     instruments into their existing row, never a double (Gordon's "No
 *     duplicate entries" rule).
 *   • Split shares follow the Rights & Splits model (lib/split-engine.ts):
 *     writing / publishing / performance pools + share of SALES (the money,
 *     basis points). Totals are checked conservatively — open offers count
 *     too — so a plan is refused before anyone could end up over 100 %.
 *   • Undo snapshots keep Firestore Timestamps through the JSON audit doc.
 */

// ── Share types ───────────────────────────────────────────────────────────────

export type ShareType = 'sales' | 'writing' | 'publishing' | 'performance';
export const SHARE_TYPES: ShareType[] = ['sales', 'writing', 'publishing', 'performance'];

type PoolField = 'writingPercent' | 'publishingPercent' | 'performancePercent';

const SHARE_ALIASES: Record<string, ShareType> = {
  sales: 'sales', sale: 'sales', money: 'sales', revenue: 'sales', income: 'sales', track: 'sales',
  salesshare: 'sales', shareofsales: 'sales', earnings: 'sales', royalties: 'sales', points: 'sales',
  writing: 'writing', songwriting: 'writing', composition: 'writing', composer: 'writing', writer: 'writing',
  publishing: 'publishing', publisher: 'publishing', pub: 'publishing',
  performance: 'performance', master: 'performance', masters: 'performance', recording: 'performance',
  neighbouring: 'performance', neighboring: 'performance', perf: 'performance',
};

/** Map what the model (or a person) called the share onto the four real share types. */
export function normalizeShareType(v: unknown): ShareType | null {
  if (typeof v !== 'string') return null;
  const k = v.toLowerCase().replace(/[^a-z]/g, '');
  return SHARE_ALIASES[k] ?? null;
}

/** 0–100 with at most 2 decimals; null when not a number. */
export function clampPercent(v: unknown): number | null {
  if (v === null || v === undefined || v === '' || typeof v === 'boolean') return null;
  const n = typeof v === 'number' ? v : Number(String(v).replace('%', '').replace(',', '.').trim());
  if (!Number.isFinite(n)) return null;
  return Math.round(Math.max(0, Math.min(100, n)) * 100) / 100;
}

const round2 = (n: number) => Math.round(n * 100) / 100;

// ── Names ─────────────────────────────────────────────────────────────────────

/** Compare key for a person's name: case-, accent- and spacing-insensitive. */
export function nameKey(s: unknown): string {
  return String(s ?? '')
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .toLowerCase().replace(/\s+/g, ' ').trim();
}
export const sameName = (a: unknown, b: unknown) => !!nameKey(a) && nameKey(a) === nameKey(b);

/** "Richard Silva II" → "Richard Silva" (II, III, IV, Jr, Sr are not part of how a person is found). */
export function stripNameSuffix(name: string): string {
  return String(name || '').replace(/[\s,]+(?:jr\.?|sr\.?|ii|iii|iv)$/i, '').trim();
}

export function titleCase(s: string): string {
  return s.replace(/\S+/g, w => w.charAt(0).toLocaleUpperCase() + w.slice(1));
}

/** Spellings to try in an exact-match Firestore `in` query (Firestore can't ignore case). */
export function nameVariants(name: string): string[] {
  const t = name.trim().replace(/\s+/g, ' ');
  if (!t) return [];
  const lower = t.toLocaleLowerCase();
  return [...new Set([t, titleCase(lower), titleCase(t), lower, t.toLocaleUpperCase()])].slice(0, 10);
}

// ── Roles ─────────────────────────────────────────────────────────────────────

const roleKey = (s: unknown) => nameKey(s).replace(/[^a-z0-9]/g, '');

/** The allowed role spelled the app's way ("mixing engineer" → "Mixing Engineer"), or null. */
export function canonicalRole(role: unknown, allowed: string[]): string | null {
  const k = roleKey(role);
  if (!k) return null;
  return allowed.find(a => roleKey(a) === k) ?? null;
}

/** Canonicalise a role list; unknown roles are reported, never silently invented. */
export function canonicalRoles(roles: unknown, allowed: string[]): { roles: string[]; unknown: string[] } {
  const raw = Array.isArray(roles) ? roles : (typeof roles === 'string' ? roles.split(',') : []);
  const out: string[] = [];
  const unknown: string[] = [];
  for (const r of raw.slice(0, 10)) {
    const s = String(r ?? '').trim();
    if (!s) continue;
    const c = canonicalRole(s, allowed);
    if (!c) { unknown.push(s.slice(0, 60)); continue; }
    if (!out.some(x => roleKey(x) === roleKey(c))) out.push(c);
  }
  return { roles: out, unknown };
}

// ── Contributors ──────────────────────────────────────────────────────────────

/** A credit row as stored on a work (`works/{id}.contributors[]`). Unknown fields are preserved. */
export interface ContributorRow {
  name: string;
  userId?: string;
  role?: string;
  roles?: string[];
  instrument?: string;
  [extra: string]: unknown;
}

export const rolesOf = (c: Partial<ContributorRow> | null | undefined): string[] =>
  Array.isArray(c?.roles) && c!.roles!.length ? c!.roles!.map(String) : (c?.role ? [String(c.role)] : []);

const splitList = (s: unknown) => String(s ?? '').split(',').map(x => x.trim()).filter(Boolean);

export interface MergeInput { name: string; roles: string[]; instrument?: string; userId?: string }
export interface MergeResult {
  list: ContributorRow[];
  outcome: 'added' | 'merged' | 'duplicate';
  index: number;
  matchedName: string;
}

/** Index of the row this person already has — same name, or the same linked account. */
export function findContributor(list: ContributorRow[], name: string, userId?: string): number {
  const byName = list.findIndex(c => sameName(c?.name, name));
  if (byName >= 0) return byName;
  return userId ? list.findIndex(c => c?.userId === userId) : -1;
}

/**
 * Add a credit, or merge the new roles/instrument into the person's existing row
 * (same rules as the Media Vault's addOrMergeContributor, plus account matching).
 * Pure: returns a NEW list; every untouched row is the same object as before.
 */
export function mergeContributor(list: ContributorRow[], input: MergeInput): MergeResult {
  const name = input.name.trim().replace(/\s+/g, ' ');
  const roles = input.roles.filter(Boolean);
  const instruments = splitList(input.instrument);
  const i = findContributor(list, name, input.userId);
  if (i < 0) {
    const row: ContributorRow = {
      name,
      ...(input.userId ? { userId: input.userId } : {}),
      role: roles[0] || '',
      roles,
      instrument: instruments.join(', '),
    };
    return { list: [...list, row], outcome: 'added', index: list.length, matchedName: name };
  }
  const existing = list[i];
  const mergedRoles = rolesOf(existing);
  let changed = false;
  for (const r of roles) if (!mergedRoles.some(x => roleKey(x) === roleKey(r))) { mergedRoles.push(r); changed = true; }
  const mergedInstr = splitList(existing.instrument);
  for (const ins of instruments) if (!mergedInstr.some(x => nameKey(x) === nameKey(ins))) { mergedInstr.push(ins); changed = true; }
  const linkUser = !existing.userId && !!input.userId;
  if (!changed && !linkUser) return { list, outcome: 'duplicate', index: i, matchedName: existing.name };
  const row: ContributorRow = {
    ...existing,
    ...(linkUser ? { userId: input.userId } : {}),
    role: mergedRoles[0] || existing.role || '',
    roles: mergedRoles,
    instrument: mergedInstr.length ? mergedInstr.join(', ') : (existing.instrument ?? ''),
  };
  const next = list.slice();
  next[i] = row;
  return { list: next, outcome: 'merged', index: i, matchedName: existing.name };
}

/** The denormalised `works.contributorUserIds` index for a credit list. */
export function contributorUserIdsOf(list: ContributorRow[]): string[] {
  return [...new Set(list.map(c => c?.userId).filter((v): v is string => typeof v === 'string' && !!v))];
}

// ── Splits ────────────────────────────────────────────────────────────────────

/** A party row of a split agreement, as lib/split-engine.ts stores it. */
export interface PartyRow {
  userId: string;
  userName?: string;
  role?: string;
  roles?: string[];
  writingPercent?: number;
  publishingPercent?: number;
  performancePercent?: number;
  salesShareBp?: number;
  creditKind?: 'split' | 'thanks';
  status?: string;
  awaiting?: string | null;
  proposal?: (Partial<PartyRow> & { by?: string; note?: string; at?: unknown }) | null;
  [extra: string]: unknown;
}

export interface SplitTerms {
  role: string;
  roles: string[];
  writingPercent: number;
  publishingPercent: number;
  performancePercent: number;
  salesShareBp: number;
  creditKind: 'split';
}

const POOL_FIELD: Record<Exclude<ShareType, 'sales'>, PoolField> = {
  writing: 'writingPercent', publishing: 'publishingPercent', performance: 'performancePercent',
};

/** A share as a percent (sales is stored as basis points). */
export function shareOf(t: Partial<PartyRow> | null | undefined, type: ShareType): number {
  if (!t) return 0;
  if (type === 'sales') return (Number.isFinite(Number(t.salesShareBp)) ? Number(t.salesShareBp) : 0) / 100;
  return Number(t[POOL_FIELD[type]]) || 0;
}

/** Terms currently in force for a party (the base a change starts from). */
function termsInForce(p: PartyRow | null): SplitTerms {
  return {
    role: String(p?.role || ''),
    roles: Array.isArray(p?.roles) && p!.roles!.length ? p!.roles!.map(String) : (p?.role ? [String(p.role)] : []),
    writingPercent: Number(p?.writingPercent) || 0,
    publishingPercent: Number(p?.publishingPercent) || 0,
    performancePercent: Number(p?.performancePercent) || 0,
    salesShareBp: Number.isInteger(p?.salesShareBp) ? Number(p!.salesShareBp) : 0,
    creditKind: 'split',
  };
}

/**
 * The full terms to propose: the person's current terms with ONE share changed.
 * `role` replaces their role (kept in their roles list); otherwise their role stays.
 */
export function buildSplitTerms(existing: PartyRow | null, type: ShareType, percent: number, role: string): SplitTerms {
  const base = termsInForce(existing);
  const r = (role || base.role).trim();
  const roles = base.roles.some(x => roleKey(x) === roleKey(r)) ? base.roles : [r, ...base.roles];
  const t: SplitTerms = { ...base, role: r, roles: roles.slice(0, 10) };
  if (type === 'sales') t.salesShareBp = Math.round(percent * 100);
  else t[POOL_FIELD[type]] = round2(percent);
  return t;
}

export interface TotalCheck {
  ok: boolean;
  /** Everyone's share of this type after the change, counting open offers. */
  total: number;
  /** Everyone else's share. */
  others: number;
  /** This person's share before the change (the larger of agreed / offered). */
  previous: number;
}

/**
 * Would this share push the total for its type over 100 %? Conservative on
 * purpose: other people's OPEN offers count as if accepted (the larger of
 * their agreed and offered terms), declined offers don't. The split engine
 * re-checks agreed terms again at accept time.
 */
export function checkShareTotal(splits: PartyRow[], partyUserId: string, type: ShareType, percent: number): TotalCheck {
  const live = (p: PartyRow) => p.status !== 'disputed';
  const valueOf = (p: PartyRow) => Math.max(shareOf(p, type), p.proposal ? shareOf(p.proposal, type) : 0);
  const others = round2(splits.filter(p => p.userId !== partyUserId && live(p)).reduce((s, p) => s + valueOf(p), 0));
  const me = splits.find(p => p.userId === partyUserId);
  const previous = me && live(me) ? round2(valueOf(me)) : 0;
  const total = round2(others + percent);
  return { ok: total <= 100.0001, total, others, previous };
}

/** Everything about a party row that an answer from the other side would change (timestamps aside). */
export function partyFingerprint(p: PartyRow | null | undefined): string {
  if (!p) return 'absent';
  const t = termsInForce(p);
  const prop = p.proposal
    ? [p.proposal.by ?? null, termsInForce(p.proposal as PartyRow), p.proposal.creditKind ?? 'split', p.proposal.note ?? null]
    : null;
  return JSON.stringify([p.userId, t, p.creditKind === 'thanks' ? 'thanks' : 'split', p.status ?? null, p.awaiting ?? null, prop]);
}

export function findPartyByName(splits: PartyRow[], name: string): PartyRow | null {
  return splits.find(p => sameName(p.userName, name)) ?? null;
}

/** Overall agreement status after a row changes (mirrors the split engine). */
export function agreementStatusFor(current: string | undefined, splits: PartyRow[]): string {
  if (current === 'frozen') return 'frozen';
  return splits.some(p => p.status === 'accepted') ? 'active' : 'draft';
}

// ── Undo snapshots ────────────────────────────────────────────────────────────

/** Replace Firestore Timestamps with a JSON-safe marker so an audit doc can carry them. */
export function freezeValue(v: unknown): unknown {
  if (v === undefined) return null;
  if (v === null || typeof v !== 'object') return v;
  const anyV = v as any;
  if (typeof anyV.toMillis === 'function' && typeof anyV.seconds === 'number' && typeof anyV.nanoseconds === 'number') {
    return { __ts: [anyV.seconds, anyV.nanoseconds] };
  }
  if (Array.isArray(v)) return v.map(freezeValue);
  const out: Record<string, unknown> = {};
  for (const [k, x] of Object.entries(v)) if (x !== undefined) out[k] = freezeValue(x);
  return out;
}

/** Inverse of freezeValue — `make` rebuilds a Timestamp from seconds + nanoseconds. */
export function thawValue(v: unknown, make: (seconds: number, nanoseconds: number) => unknown): unknown {
  if (v === null || typeof v !== 'object') return v;
  if (Array.isArray(v)) return v.map(x => thawValue(x, make));
  const ts = (v as any).__ts;
  if (Array.isArray(ts) && ts.length === 2 && Object.keys(v).length === 1) return make(Number(ts[0]), Number(ts[1]));
  const out: Record<string, unknown> = {};
  for (const [k, x] of Object.entries(v)) out[k] = thawValue(x, make);
  return out;
}

// ── Finding works by title in what was said ───────────────────────────────────

/**
 * Candidate track titles from a spoken/typed request — every run of 1–6 words,
 * as typed and Title Cased ("lägg till desmond på keep climbing" → …"Keep
 * Climbing"…). Longer runs first; quoted titles before everything.
 */
export function titleCandidates(message: string, max = 150): string[] {
  const out: string[] = [];
  const push = (s: string) => {
    const t = s.trim().replace(/\s+/g, ' ');
    if (t.length >= 2 && t.length <= 120 && !out.includes(t)) out.push(t);
  };
  for (const m of message.matchAll(/["“”«»'‘’]([^"“”«»'‘’]{2,120})["“”«»'‘’]/g)) { push(m[1]); push(titleCase(m[1].toLocaleLowerCase())); }
  const words = message
    .split(/\s+/)
    .map(w => w.replace(/^[^\p{L}\p{N}]+|[^\p{L}\p{N}!?']+$/gu, ''))
    .filter(Boolean);
  if (words.length > 40) return out.slice(0, max);
  for (let n = Math.min(6, words.length); n >= 1; n--) {
    for (let i = 0; i + n <= words.length; i++) {
      const run = words.slice(i, i + n).join(' ');
      if (n === 1 && run.length < 3) continue;
      push(run);
      push(titleCase(run.toLocaleLowerCase()));
    }
  }
  return out.slice(0, max);
}

/** JSON with sorted object keys — compares Firestore maps whose key order isn't guaranteed. */
export function stableStringify(v: unknown): string {
  const norm = (x: unknown): unknown => {
    if (x === null || typeof x !== 'object') return x === undefined ? null : x;
    if (Array.isArray(x)) return x.map(norm);
    const anyX = x as any;
    if (typeof anyX.toMillis === 'function') return { __ts: anyX.toMillis() };
    return Object.fromEntries(Object.keys(x).sort().filter(k => (x as any)[k] !== undefined).map(k => [k, norm((x as any)[k])]));
  };
  return JSON.stringify(norm(v));
}
